import { FiActivity } from 'react-icons/fi';
import { LuWrench } from 'react-icons/lu';
import { CiWifiOff } from 'react-icons/ci';
import { BiSolidNoEntry } from 'react-icons/bi';
import { FaClipboardList } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { SortKey, SystemStatus, SystemStatusColors } from '../types';

/* ---------------------- System Status Colors ---------------------- */

export const SYSTEM_STATUS_COLORS: Record<SystemStatus, SystemStatusColors> = {
  [SystemStatus.OPERATIONAL]: {
    core: '#1db870',
    background: '#163a28',
    bright: '#3ddc8c',
  },
  [SystemStatus.MAINTENANCE]: {
    core: '#d49018',
    background: '#3a2a06',
    bright: '#ffca28',
  },
  [SystemStatus.GROUNDED]: {
    core: '#4a9edd',
    background: '#0e2236',
    bright: '#74bcf7',
  },
  [SystemStatus.OFFLINE]: {
    core: '#787878',
    background: '#2e2e2e',
    bright: '#909090',
  },
  [SystemStatus.PLANNED]: {
    core: '#c2c2c2',
    background: '#2a2a2a',
    bright: '#e8e8e8',
    outline: '#ffffff',
    markerColor: '#000000',
    markerTextColor: '#ffffff',
  },
};

/* ---------------------- System Status Configs ---------------------- */

export interface SystemStatusConfig {
  status: SystemStatus;
  label: string;
  icon: IconType;
  iconStrokeWidth: number;
  colors: SystemStatusColors;
}

export const SYSTEM_STATUS_CONFIGS: SystemStatusConfig[] = [
  {
    status: SystemStatus.OPERATIONAL,
    label: 'OPERATIONAL',
    icon: FiActivity,
    iconStrokeWidth: 2,
    colors: SYSTEM_STATUS_COLORS[SystemStatus.OPERATIONAL],
  },
  {
    status: SystemStatus.MAINTENANCE,
    label: 'MAINTENANCE',
    icon: LuWrench,
    iconStrokeWidth: 1.5,
    colors: SYSTEM_STATUS_COLORS[SystemStatus.MAINTENANCE],
  },
  {
    status: SystemStatus.GROUNDED,
    label: 'GROUNDED',
    icon: BiSolidNoEntry,
    iconStrokeWidth: 0.75,
    colors: SYSTEM_STATUS_COLORS[SystemStatus.GROUNDED],
  },
  {
    status: SystemStatus.OFFLINE,
    label: 'OFFLINE',
    icon: CiWifiOff,
    iconStrokeWidth: 1,
    colors: SYSTEM_STATUS_COLORS[SystemStatus.OFFLINE],
  },
  {
    status: SystemStatus.PLANNED,
    label: 'PLANNED',
    icon: FaClipboardList,
    iconStrokeWidth: 0.75,
    colors: SYSTEM_STATUS_COLORS[SystemStatus.PLANNED],
  },
];

/* ---------------------- Sorting Options ---------------------- */

export const BASE_SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: SortKey.MOST_MISSIONS, label: 'Most Missions' },
  { key: SortKey.AVERAGE_MISSION_DURATION, label: 'Avg Mission Time' },
  { key: SortKey.NUMBER_OF_NIGHT_MISSIONS, label: 'Night Missions' },
  { key: SortKey.NUMBER_OF_DAY_MISSIONS, label: 'Day Missions' },
];

/* ---------------------- Layout Values ---------------------- */

export const DASHBOARD_LAYOUT = {
  assetRosterPreferredWidth: 'w-80',
  assetRosterMinWidth: 'min-w-[100px]',
  assetRosterGridBreakpoint: 'min-[300px]:grid-cols-2',
};

/* ---------------------- WebSocket Base Status Mappings ---------------------- */

/**
 * Maps raw base status strings from the WebSocket to their canonical display labels that
 * match the strings used in the status sets (OPERATIONAL_BASE_STATUSES, etc).
 * Keys are always lowercase so the lookup is case-insensitive.
 */
export const WS_BASE_STATUS_NORMALIZED_MAP: Record<string, string> = {
  // No Status
  'no-status': 'No Status',
  'no status': 'No Status',

  // VP
  vp: 'VP (Validation Provisioning)',
  'vp (validation provisioning)': 'VP (Validation Provisioning)',

  // Validation
  validation: 'Validation',

  // DR
  dr: 'DR (Hardware Deployment Review)',
  'dr (hardware deployment review)': 'DR (Hardware Deployment Review)',

  // DP
  dp: 'DP (Deployment Provisioning)',
  'dp (deployment provisioning)': 'DP (Deployment Provisioning)',

  // PR
  pr: 'PR (Package and Packaging Review)',
  'pr (package and packaging review)': 'PR (Package and Packaging Review)',

  // Planned Deployment
  'planned deployment': 'Planned Deployment',
  'planned-deployment': 'Planned Deployment',

  // Deployed Operational
  'deployed operational': 'Deployed Operational',
  'deployed-operational': 'Deployed Operational',

  // Deployed Backup
  'deployed backup': 'Deployed Backup',
  'deployed-backup': 'Deployed Backup',

  // Outbound Shipping / Storage
  'outbound-shipping-or-storage': 'Outbound Shipping / Storage',
  'outbound shipping / storage': 'Outbound Shipping / Storage',
  'outbound shipping/storage': 'Outbound Shipping / Storage',

  // On-site Unboxing
  'on-site-unboxing': 'On-site Unboxing',
  'on-site unboxing': 'On-site Unboxing',
  'on site unboxing': 'On-site Unboxing',

  // On-site System Installation
  'on-site-system-installation': 'On-site System Installation',
  'on-site system installation': 'On-site System Installation',
  'on site system installation': 'On-site System Installation',

  // Deployment Customer Validation
  'deployment-customer-validation': 'Deployment Customer Validation',
  'deployment customer validation': 'Deployment Customer Validation',

  // Training and Handoff to Customer
  'training-and-handoff-to-customer': 'Training and Handoff to Customer',
  'training and handoff to customer': 'Training and Handoff to Customer',

  // Customer Operation
  'customer-operation': 'Customer Operation',
  'customer operation': 'Customer Operation',

  // Periodic System Maintenance / On-Site Inspection
  'periodic-system-maintenance-or-on-site-inspection':
    'Periodic System Maintenance / On-Site Inspection',
  'periodic system maintenance / on-site inspection':
    'Periodic System Maintenance / On-Site Inspection',
  'periodic system maintenance / on site inspection':
    'Periodic System Maintenance / On-Site Inspection',

  // On-Site Repair
  'on-site-repair': 'On-Site Repair',
  'on-site repair': 'On-Site Repair',
  'on site repair': 'On-Site Repair',

  // On-site Validation
  'on-site-validation': 'On-site Validation',
  'on-site validation': 'On-site Validation',
  'on site validation': 'On-site Validation',

  // Base Arrival Review
  'base-arrival-review': 'Base Arrival Review',
  'base arrival review': 'Base Arrival Review',

  // Base-MRU — canonical form in MAINTENANCE_BASE_STATUSES is 'Base-MRU'
  'base-mru': 'Base-MRU',
  'base mru': 'Base-MRU',

  // Retired
  retired: 'Retired',
};
